import { useAuthStore } from './authStore';
import { authHeartbeat } from './api';
import { reportClientError } from './error-reporter';

// The "closed > 5 min" detector. This is a UX optimization — it shows the lock
// proactively on return without first eating a failed request, and keeps the
// session alive while the app is open & visible. It is NOT the security
// boundary: the authoritative lock is the server's 5-min session TTL, surfaced
// as a 401 on the next API call (caught by the global fetch interceptor). The
// client never declares itself *unlocked* — only a server-verified WebAuthn
// assertion does that.

const HEARTBEAT_MS = 60 * 1000;
// While the server is unreachable (mid-deploy / transient outage), probe faster
// so the app resumes promptly once it returns — then fall back to the calm 60s.
const HEARTBEAT_RETRY_MS = 10 * 1000;
// The lock-after window is device-aware (mobile 10 min / desktop 60 min) and is
// the server-confirmed value held in the auth store.
const lockAfterMs = () => useAuthStore.getState().lockAfterMs;

let heartbeatTimer: number | null = null;
let hiddenAt = 0;
let started = false;
// Whether the last probe saw the server as unreachable (drives the faster retry
// cadence). NEVER causes a lock — only a genuine 401 does.
let unreachable = false;

const isActive = () => useAuthStore.getState().status === 'authenticated';

// Breadcrumb only the auto-locks that look like a REGRESSION — via the always-on
// client-error path (sendBeacon → /api/client-errors, which bypasses the 401
// interceptor). An isolated auto-lock is expected (a real >lock-window absence
// locks exactly once) and reporting it would just flood Diagnostics; so we stay
// silent on it and surface only a lock that RE-FIRES suspiciously soon after
// another. A genuine re-idle can't recur that fast — you'd have to be away a full
// lock window again — so a fast re-fire is the signature of the mobile re-lock
// loop b-00256 fixed. Unlock is an in-place SPA transition (no reload), so this
// timestamp survives a lock→unlock→re-lock cycle within the page load; a full
// reload resets it (fine — detection is purely intra-page-load). Timed off
// performance.now() (monotonic) NOT Date.now(): a backward wall-clock jump must
// never turn two legitimate isolated locks into a negative delta that slips past
// the window and reports a phantom regression. Never throws.
let lastAutoLockAt = 0;
// Comfortably above the loop's ~12–15s cadence yet far below the smallest lock
// window (10 min mobile), so no legitimate second idle-lock can fall inside it.
const RELOCK_SUSPECT_MS = 2 * 60 * 1000;

function reportAutoLock(reason: string, detail: Record<string, unknown> = {}): void {
  try {
    const nowMono = performance.now();
    const sinceLastLockMs = lastAutoLockAt ? Math.round(nowMono - lastAutoLockAt) : Infinity;
    lastAutoLockAt = nowMono;
    if (sinceLastLockMs > RELOCK_SUSPECT_MS) return; // expected, isolated lock — silent
    const ctx = {
      reason,
      sinceLastLockMs,
      path: location.pathname + location.search,
      visibility: document.visibilityState,
      msSinceVisible: Date.now() - useAuthStore.getState().lastVisibleAt,
      lockAfterMs: lockAfterMs(),
      ...detail,
    };
    reportClientError(
      `auto-lock re-fired ${sinceLastLockMs}ms after previous (possible re-lock regression) — ${reason}: ${JSON.stringify(ctx)}`,
      { kind: 'auth.autolock' },
    );
  } catch {
    /* a breadcrumb must never break the lock path */
  }
}

function stopHeartbeat() {
  if (heartbeatTimer !== null) {
    clearTimeout(heartbeatTimer);
    heartbeatTimer = null;
  }
}

// Arm the next probe. Always cancels any pending timer first so there is never
// more than one live timer. Faster cadence while the server is unreachable, then
// relax to the calm 60s heartbeat once it answers.
function scheduleNext() {
  stopHeartbeat();
  heartbeatTimer = window.setTimeout(
    () => void tick(),
    unreachable ? HEARTBEAT_RETRY_MS : HEARTBEAT_MS,
  );
}

// Start (or restart) the heartbeat loop now: a fresh probe that then
// self-schedules. Resets the fast-retry flag so a new visible/active entry
// begins on the calm cadence.
function kick() {
  stopHeartbeat();
  unreachable = false;
  void tick();
}

async function tick() {
  // Only while genuinely visible + active. Never heartbeat while hidden, or we'd
  // keep the session alive in the background and defeat the closed-5-min lock.
  if (document.visibilityState !== 'visible' || !isActive()) return;
  useAuthStore.getState().markVisible();
  const result = await authHeartbeat();
  // The await may have spanned a hide, or a lock from another path (e.g. the 401
  // interceptor on a concurrent API call) — re-check before acting/rescheduling.
  if (document.visibilityState !== 'visible' || !isActive()) return;
  if (result === 'expired') {
    // Authoritative lapse — lock. (Mirrors what the 401 interceptor would do.)
    reportAutoLock('heartbeat-expired');
    useAuthStore.getState().lock();
    return; // stop the loop; the lock screen takes over.
  }
  // 'ok' → calm cadence; 'unreachable' → faster retry, but DO NOT lock. The app
  // stays mounted and waits calmly for the server to come back.
  unreachable = result === 'unreachable';
  scheduleNext();
}

function onVisible() {
  useAuthStore.getState().markVisible();
  if (!isActive()) return;
  // CONSUME hiddenAt: measure the away-duration from the LAST hide, then clear it
  // so a later focus/visibility event with no intervening hide can never re-lock.
  // This is load-bearing on mobile: blur/focus fire WITHOUT a visibilitychange→
  // hidden (soft-keyboard toggle, the biometric sheet dismissing, notification
  // banners), so a stale hiddenAt would re-fire this elapsed check on every focus
  // once wall-clock passed the lock window — re-locking immediately after each
  // unlock (the mobile "re-locks 4-5×/min while active" loop). Reset-on-visible
  // keeps the intended behavior: a >lock-window absence still locks exactly once,
  // on the first visible after a real onHidden.
  // Clamp: a backward clock jump → 0 (don't lock spuriously); a huge forward jump
  // → lock (fail safe). No prior hide (hiddenAt 0) → elapsed 0.
  const wasHiddenAt = hiddenAt;
  hiddenAt = 0;
  const elapsed = wasHiddenAt ? Math.max(0, Date.now() - wasHiddenAt) : 0;
  if (elapsed > lockAfterMs()) {
    reportAutoLock('visible-idle-elapsed', { elapsed });
    useAuthStore.getState().lock();
    return;
  }
  kick();
}

function onHidden() {
  // Record when we left visibility. Do NOT markVisible() here — that would reset
  // the persisted lastVisibleAt to "now" and defeat the cold-start >5-min check.
  hiddenAt = Date.now();
  stopHeartbeat();
}

export function startIdleLock(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') onVisible();
    else onHidden();
  });
  // bfcache / app close.
  window.addEventListener('pagehide', onHidden);
  window.addEventListener('pageshow', (e) => {
    if ((e as PageTransitionEvent).persisted) onVisible();
  });
  // Page Lifecycle freeze/resume (Chromium); harmless where unsupported.
  window.addEventListener('freeze', onHidden as EventListener);
  window.addEventListener('resume', onVisible as EventListener);
  window.addEventListener('focus', () => {
    if (document.visibilityState === 'visible') onVisible();
  });

  // Drive the heartbeat off the auth status. Critically this starts it the moment
  // we become authenticated even if the user never leaves the tab (no
  // visibilitychange/focus event ever fires) — otherwise a continuously-visible
  // session is only kept alive by incidental API calls and wrongly locks after
  // 5 min of light use. Guard on STATUS transitions so markVisible()'s own state
  // writes (status unchanged) can't recurse.
  useAuthStore.subscribe((state, prev) => {
    if (state.status === prev.status) return;
    if (state.status === 'authenticated') {
      if (document.visibilityState === 'visible') {
        kick();
      }
    } else {
      stopHeartbeat();
    }
  });

  if (isActive() && document.visibilityState === 'visible') {
    kick();
  }
}
